/**
 * Date utility functions for working with YYYY-MM-DD formatted date strings.
 * These are used throughout the app for order and product date filtering.
 */

import { getISODay } from 'date-fns';

/**
 * Format a Date object as YYYY-MM-DD string in local timezone
 * @param date - The date to format
 * @returns Date string in YYYY-MM-DD format
 */
export function formatDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get today's date as YYYY-MM-DD string in local timezone
 * @returns Today's date in YYYY-MM-DD format
 */
export function getTodayKey(): string {
  return formatDateKey(new Date());
}

/**
 * Add or subtract days from a YYYY-MM-DD date string
 * @param dateKey - Date string in YYYY-MM-DD format
 * @param days - Number of days to add (positive) or subtract (negative)
 * @returns New date string in YYYY-MM-DD format
 */
export function adjustDateByDays(dateKey: string, days: number): string {
  // Append T12:00:00 to avoid timezone issues when parsing
  const date = new Date(dateKey + 'T12:00:00');
  date.setDate(date.getDate() + days);
  return formatDateKey(date);
}

/**
 * Get the previous day from a YYYY-MM-DD date string
 * @param dateKey - Date string in YYYY-MM-DD format
 * @returns Previous date in YYYY-MM-DD format
 */
export function getPreviousDay(dateKey: string): string {
  return adjustDateByDays(dateKey, -1);
}

/**
 * Get the next day from a YYYY-MM-DD date string
 * @param dateKey - Date string in YYYY-MM-DD format
 * @returns Next date in YYYY-MM-DD format
 */
export function getNextDay(dateKey: string): string {
  return adjustDateByDays(dateKey, 1);
}

/**
 * Calculate the next occurrence of a given day of week
 * @param dayOfWeek - Day of week (ISO 8601: 1=Monday, 2=Tuesday, ..., 7=Sunday)
 * @param fromDate - Starting date (defaults to today)
 * @returns Date string in YYYY-MM-DD format of the next occurrence (today if it matches)
 */
export function getNextDayOfWeek(dayOfWeek: number, fromDate: Date = new Date()): string {
  const currentDay = getISODay(fromDate); // ISO: 1=Monday, 7=Sunday

  // Calculate days until the target day
  let daysUntil = dayOfWeek - currentDay;

  // If the target day is today or in the past this week, we still return today if it matches
  // Otherwise, move to next week
  if (daysUntil < 0) {
    daysUntil += 7;
  }

  // Create a new date and add the days
  const targetDate = new Date(fromDate);
  targetDate.setDate(targetDate.getDate() + daysUntil);

  return formatDateKey(targetDate);
}

/**
 * Check if a date falls within an absence period
 * @param date - Date string in YYYY-MM-DD format to check
 * @param absenceStart - Start of absence period in YYYY-MM-DD format (inclusive)
 * @param absenceEnd - End of absence period in YYYY-MM-DD format (inclusive)
 * @returns true if the date is within the absence period
 */
export function isDateInAbsencePeriod(
  date: string,
  absenceStart?: string,
  absenceEnd?: string
): boolean {
  if (!absenceStart || !absenceEnd) {
    return false;
  }
  // Lexicographic comparison works for YYYY-MM-DD format
  return date >= absenceStart && date <= absenceEnd;
}

/**
 * Find the next occurrence of a day of week that falls after an absence period ends
 * @param dayOfWeek - Day of week (ISO 8601: 1=Monday, 2=Tuesday, ..., 7=Sunday)
 * @param absenceEnd - End of absence period in YYYY-MM-DD format
 * @returns Date string in YYYY-MM-DD format of the first occurrence after absence
 */
export function getNextDayOfWeekAfterAbsence(
  dayOfWeek: number,
  absenceEnd: string
): string {
  // Start searching from the day after absence ends
  const dayAfterAbsence = new Date(absenceEnd + 'T12:00:00');
  dayAfterAbsence.setDate(dayAfterAbsence.getDate() + 1);

  return getNextDayOfWeek(dayOfWeek, dayAfterAbsence);
}
